import React from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ViewStyle,
  StyleProp,
} from 'react-native';
import { Text } from '@/components/common/text';
import { MOBILE_TYPOGRAPHY } from '@/constants/residents/mobile/mobileTypography';

interface PurpleBadgeProps {
  /** Badge text label */
  label: string;
  /** Handler when badge is pressed (makes it a button) */
  onPress?: () => void;
  /** Badge border/text color (default: #CB7ED3) */
  color?: string;
  /** Badge background color (default: rgba(23, 23, 23, 0.5)) */
  backgroundColor?: string;
  /** Custom container style */
  style?: StyleProp<ViewStyle>;
  /** Accessibility label override */
  accessibilityLabel?: string;
}

/**
 * PurpleBadge
 * 
 * Reusable badge component with purple outline styling.
 * Used for WiFi card "Having issues?" button and "Wi-Fi network" badge.
 * Matches Figma design for accent badges.
 * 
 * @example
 * // Static badge
 * <PurpleBadge label="Wi-Fi network" />
 * 
 * @example
 * // Clickable badge
 * <PurpleBadge
 *   label="Having issues?"
 *   onPress={handleHavingIssuesPress}
 * />
 */
export const PurpleBadge: React.FC<PurpleBadgeProps> = ({
  label,
  onPress,
  color = '#CB7ED3',
  backgroundColor = 'rgba(23, 23, 23, 0.5)',
  style,
  accessibilityLabel,
}) => {
  const Container = onPress ? TouchableOpacity : View;

  return (
    <Container
      style={[
        styles.badge,
        { backgroundColor, borderColor: color },
        style,
      ]}
      onPress={onPress}
      accessibilityRole={onPress ? 'button' : undefined}
      accessibilityLabel={accessibilityLabel || label}
    >
      <Text style={[styles.text, { color }]}>{label}</Text>
    </Container>
  );
};

const styles = StyleSheet.create({
  badge: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    minHeight: 28,
    paddingVertical: 4, // 28 − 18 line − 2×1 border, split
    justifyContent: 'center',
    alignItems: 'center',
  },
  text: {
    ...MOBILE_TYPOGRAPHY.label,
  },
});

export default PurpleBadge;
